/**
 * @file Property and unit input schemas (T1.1).
 *
 * These live in the kernel because the API validates with them and the SPA shows
 * the same field rules; PLAN.md §4 puts "zod schemas" in packages/shared.
 *
 * One deliberate rule about how the SPA uses them: the web app imports only the
 * TYPES (`import type`), never the schemas as values. The API is the authority on
 * what is valid, and a browser bundle that shipped its own copy of these rules
 * would be a second, drifting authority. The SPA gets HTML-level constraints
 * (required, maxLength) and renders the server's error envelope — the same
 * pattern the rest of the shell already uses.
 *
 * Parsing is used at the ROUTE boundary on raw JSON (`safeParse` on an unknown
 * body), so every schema here is written to be total over `unknown`, and to
 * normalise as it validates: trimming, upper-casing country codes, and turning an
 * empty optional field into the `null` the database stores rather than `""`,
 * which would otherwise be a distinct, meaningless value in the column.
 */
import { z } from "zod";

/**
 * Fixed-offset zone ids allowed on their own.
 *
 * Only UTC: it is a real, unambiguous choice (and the one tests use). Everything
 * else without a region is a legacy abbreviation, rejected below.
 */
const FIXED_OFFSET_ZONES = new Set(["UTC"]);

/** The canonical zone list, computed once — `supportedValuesOf` is not free. */
let canonicalZones: Set<string> | null | undefined;

function canonicalTimeZones(): Set<string> | null {
  if (canonicalZones !== undefined) return canonicalZones;
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.("timeZone");
    canonicalZones = supported === undefined ? null : new Set(supported);
  } catch {
    canonicalZones = null;
  }
  return canonicalZones;
}

/**
 * Is this a timezone the platform should accept for a property?
 *
 * Not merely "does `Intl` accept it". `Intl.DateTimeFormat` accepts `EST`, and
 * `EST` IS in the tzdata database — as a backwards-compatibility link to a FIXED
 * UTC-5 offset. A Michigan landlord typing `EST` would therefore get a property
 * that is an hour off for the eight months of the year the state observes DST,
 * and PLAN.md §6.4 uses this column to decide which local day a rent charge
 * falls on. So the rule is: a REGION-based canonical zone (`America/Detroit`),
 * or UTC. `EST`, `MST`, `HST` and `EST5EDT` are refused.
 *
 * Region-based is required structurally ("Area/Location") rather than checked
 * against a hand-written list, so the answer does not depend on which ICU data
 * the runtime happens to ship — Node locally, V8 on Workers. `Intl.supportedValuesOf`
 * is used when present because it is exact; the fallback reaches the same
 * conclusion for legacy abbreviations without it.
 */
export function isIanaTimeZone(value: string): boolean {
  if (FIXED_OFFSET_ZONES.has(value)) return true;

  const canonical = canonicalTimeZones();
  if (canonical !== null) return canonical.has(value);

  // No canonical list on this runtime: a formatter must accept it AND it must
  // look like a region id. Both halves matter — the first rejects typos, the
  // second rejects the legacy fixed-offset links the first would allow.
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
  } catch {
    return false;
  }
  return value.includes("/");
}

/** Required, trimmed, length-bounded text. */
function requiredText(max: number) {
  return z.string().trim().min(1, "is required").max(max);
}

/**
 * Optional text that is stored as `null` when absent or blank.
 *
 * The transform is the point: without it a cleared form field would persist as
 * `""`, so "no rental licence" would have two representations and every later
 * query would have to remember both.
 */
function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => (value === undefined || value === null || value.length === 0 ? null : value));
}

export const PROPERTY_TIMEZONE_HINT =
  "must be an IANA timezone, e.g. America/Detroit";

/**
 * The kind of building, which is one fact that implies others.
 *
 * `unitCount` is what makes this worth a lookup rather than free text: choosing
 * "Duplex" is how a landlord says "two units" without being asked twice, and the
 * add-property form uses exactly this to decide how many unit rows to render.
 * `multi_family` and `other` are `null` because the count genuinely varies — the
 * form asks in those cases instead of guessing.
 */
export const PROPERTY_TYPES = [
  { value: "single_family", label: "Single family", hint: "One unit", unitCount: 1 },
  { value: "duplex", label: "Duplex", hint: "Two units", unitCount: 2 },
  { value: "triplex", label: "Triplex", hint: "Three units", unitCount: 3 },
  { value: "quadplex", label: "Quadplex", hint: "Four units", unitCount: 4 },
  {
    value: "multi_family",
    label: "Multi-family (5+)",
    hint: "You pick how many",
    unitCount: null,
  },
  { value: "other", label: "Other", hint: "You pick how many", unitCount: null },
] as const;

export type PropertyType = (typeof PROPERTY_TYPES)[number]["value"];

export const PROPERTY_TYPE_VALUES = PROPERTY_TYPES.map((type) => type.value) as [
  PropertyType,
  ...PropertyType[],
];

/** How many units a type implies, or null when the landlord must say. */
export function unitCountForPropertyType(value: PropertyType): number | null {
  return PROPERTY_TYPES.find((type) => type.value === value)?.unitCount ?? null;
}

/** A sensible starting label for the nth unit of a building. */
export function defaultUnitLabel(index: number, total: number): string {
  // A single-family home is not "Unit 1" to its owner; it is the house.
  if (total === 1) return "Main house";
  return `Unit ${String(index + 1)}`;
}

/**
 * The unit amenities worth asking about.
 *
 * Curated, not free text. "W/D", "washer" and "laundry" are the same fact written
 * three ways, and a filter over free text finds none of them. Each is a question a
 * small landlord actually answers when listing a unit, and the list is short on
 * purpose — a form with thirty checkboxes gets abandoned.
 *
 * Adding an amenity is a change HERE, not a migration: `units.amenities` is a JSONB
 * array whose contents this schema validates.
 */
export const UNIT_AMENITIES = [
  { value: "in_unit_laundry", label: "In-unit laundry" },
  { value: "laundry_hookup", label: "Laundry hookup" },
  { value: "dishwasher", label: "Dishwasher" },
  { value: "garage", label: "Garage" },
  { value: "off_street_parking", label: "Off-street parking" },
  { value: "air_conditioning", label: "Air conditioning" },
  { value: "balcony_patio", label: "Balcony / patio" },
  { value: "basement", label: "Basement" },
  { value: "furnished", label: "Furnished" },
  { value: "hardwood_floors", label: "Hardwood floors" },
] as const;

export type UnitAmenity = (typeof UNIT_AMENITIES)[number]["value"];

export const UNIT_AMENITY_VALUES = UNIT_AMENITIES.map((a) => a.value) as [
  UnitAmenity,
  ...UnitAmenity[],
];

/** POST /workspaces/:workspaceId/properties body. */
export const propertyCreateSchema = z.object({
  label: optionalText(120),
  addressLine1: requiredText(200),
  addressLine2: optionalText(200),
  city: requiredText(120),
  state: requiredText(60),
  postalCode: requiredText(20),
  country: z.string().trim().length(2, "must be a 2-letter country code").toUpperCase().default("US"),
  timezone: z.string().trim().min(1, "is required").refine(isIanaTimeZone, PROPERTY_TIMEZONE_HINT),
  rentalLicenseNo: optionalText(60),
  /**
   * Optional at the schema level even though the form asks: existing rows predate
   * the column (migration 0004), and a required field would make every one of them
   * unpatchable until someone guessed a type for it.
   */
  propertyType: z.enum(PROPERTY_TYPE_VALUES).nullish(),
});

/**
 * PATCH body. `.partial()` so a caller may send one field, and note that it is
 * `partial()` on the CREATE schema rather than a second hand-written shape: a
 * field added to create is then updatable by default, and cannot be forgotten.
 */
export const propertyUpdateSchema = propertyCreateSchema.partial();

/** POST /workspaces/:workspaceId/properties/:propertyId/units body. */
export const unitCreateSchema = z.object({
  label: requiredText(60),
  beds: z.number().int("must be a whole number").min(0).max(50).nullish(),
  baths: z.number().min(0).max(50).nullish(),
  /**
   * Curated amenity values, deduplicated.
   *
   * A closed set rather than free text: free text gives you "W/D", "washer" and
   * "laundry" for one fact, after which nothing can filter on it. `.default([])`
   * keeps "no amenities recorded" and "empty list" the same thing.
   */
  amenities: z
    .array(z.enum(UNIT_AMENITY_VALUES))
    .max(UNIT_AMENITIES.length)
    .default([])
    .transform((values) => [...new Set(values)]),
});

export const unitUpdateSchema = unitCreateSchema.partial();

/** Money in integer cents, optional, never negative (CLAUDE.md: no floats). */
const moneyCents = z
  .number()
  .int("money must be whole cents — no decimals")
  .min(0, "cannot be negative")
  .nullish();

/**
 * A property's ownership economics.
 *
 * Manual entry for now. These are the numbers that turn "rent collected" into
 * "what this building actually earns": a mortgage and a tax bill are what make a
 * property a business rather than a list of units. A bank feed (MoneyKit or
 * similar) will later attach TRANSACTIONS to a property, which is a different
 * thing from this profile — see the note in migration 0004.
 */
export const propertyFinancialsSchema = z.object({
  purchasePriceCents: moneyCents,
  /**
   * A date, or blank for "not recorded".
   *
   * The empty string is allowed BEFORE the format check, not after: a `.regex()`
   * first rejects `""` with a format error, so a caller clearing the field would
   * get "must be a YYYY-MM-DD date" for leaving it empty. `refine` is used instead
   * so the blank case is normalised to null like every other optional field.
   */
  purchaseDate: z
    .string()
    .trim()
    .refine(
      (value) => value === "" || /^\d{4}-\d{2}-\d{2}$/.test(value),
      "must be a YYYY-MM-DD date",
    )
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    ),
  currentValueCents: moneyCents,
  mortgageBalanceCents: moneyCents,
  monthlyMortgageCents: moneyCents,
  annualPropertyTaxCents: moneyCents,
  annualInsuranceCents: moneyCents,
  monthlyHoaCents: moneyCents,
  monthlyOtherCents: moneyCents,
  financialNotes: optionalText(2000),
});

/**
 * POST /workspaces/:workspaceId/properties with its units, in ONE request.
 *
 * The add-property form asks for the building type and then for each unit, so the
 * units have to be part of the same write. Sending N follow-up POSTs would be N
 * transactions: a landlord who lost connectivity halfway would be left with a
 * quadplex holding two units and no indication anything went wrong. One request,
 * one transaction, all-or-nothing.
 */
export const propertyCreateWithUnitsSchema = propertyCreateSchema
  .extend({ units: z.array(unitCreateSchema).max(50, "that is a lot of units").default([]) })
  .extend(propertyFinancialsSchema.shape);

export type PropertyCreateInput = z.infer<typeof propertyCreateSchema>;
export type PropertyUpdateInput = z.infer<typeof propertyUpdateSchema>;
export type UnitCreateInput = z.infer<typeof unitCreateSchema>;
export type UnitUpdateInput = z.infer<typeof unitUpdateSchema>;
export type PropertyFinancialsInput = z.infer<typeof propertyFinancialsSchema>;
export type PropertyCreateWithUnitsInput = z.infer<typeof propertyCreateWithUnitsSchema>;

/**
 * The PRE-parse shapes, for the SPA to build request bodies from.
 *
 * `z.input` rather than `z.infer` because it reflects what a caller may actually
 * send: a field with a `.default()` (country) or a `.transform()` (the optional
 * texts) is optional on the way in and total on the way out. Derived from the
 * same schema, so the client and the validator cannot drift — which is the whole
 * reason the SPA imports these as types instead of hand-writing them.
 */
export type PropertyCreateBody = z.input<typeof propertyCreateWithUnitsSchema>;
export type PropertyUpdateBody = z.input<typeof propertyUpdateSchema>;
export type UnitCreateBody = z.input<typeof unitCreateSchema>;
export type UnitUpdateBody = z.input<typeof unitUpdateSchema>;

/**
 * A property as the API returns it.
 *
 * Declared here rather than inferred from the drizzle schema so the kernel does
 * not depend on a database package (dependency direction: apps → packages). The
 * route is responsible for producing exactly this shape.
 */
export interface PropertyDto {
  readonly id: string;
  readonly label: string | null;
  readonly addressLine1: string;
  readonly addressLine2: string | null;
  readonly city: string;
  readonly state: string;
  readonly postalCode: string;
  readonly country: string;
  readonly timezone: string;
  readonly rentalLicenseNo: string | null;
  readonly propertyType: PropertyType | null;
  /** Manual ownership economics, in integer cents. Null until filled in. */
  readonly purchasePriceCents: number | null;
  readonly purchaseDate: string | null;
  readonly currentValueCents: number | null;
  readonly mortgageBalanceCents: number | null;
  readonly monthlyMortgageCents: number | null;
  readonly annualPropertyTaxCents: number | null;
  readonly annualInsuranceCents: number | null;
  readonly monthlyHoaCents: number | null;
  readonly monthlyOtherCents: number | null;
  readonly financialNotes: string | null;
}

export interface UnitDto {
  readonly id: string;
  readonly propertyId: string;
  readonly label: string;
  readonly beds: number | null;
  readonly baths: number | null;
  readonly amenities: readonly UnitAmenity[];
}

/**
 * A unit plus the property it sits in, for the portfolio-wide list.
 *
 * The Units & beds screen answers "what can I rent out, and how big is it"
 * across every building, so each row needs enough property context to be
 * identifiable without a second request. The alternative — fetching each
 * property's units — is the N+1 shape the overview route already exists to
 * avoid.
 */
export interface UnitWithPropertyDto extends UnitDto {
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
}

/** A property with its units, for the detail screen (one round trip). */
export interface PropertyWithUnitsDto extends PropertyDto {
  readonly units: readonly UnitDto[];
}
